import { debounce, get, upperFirst } from 'lodash'

import { RequisitesBases, RequisitesPositions, Services, getDeclensions, getServicePathToAPI, isEmpty, isValidEmail, joinURLPath } from '@sigma-frontend-libs/commons'
import { isUUID } from '@sigmasms/commons'
import { RequisiteTypes } from '@sigmasms/shared-types'

import { getRUDate, globalErrorHandler, isValidPhone, proxy, request } from '@/utils'

import { bikRegExp, indexRegExp, innRegExp, ksRegExp, ogrnRegExp, rsRegExp } from '@/components/services/requisites/utils'

import render from './render'

const dadataPathToAPI = getServicePathToAPI(Services.dadata)
const usersPathToAPI = getServicePathToAPI(Services.users)

export default {
  name: 'Form',

  mixins: [ proxy({ type: 'object' }) ],

  props: {
    OwnerId: String,
    isNew: Boolean
  },

  data() {
    return {
      contactMatches: true,

      Owner: undefined,

      suggestion: {
        inn: undefined,
        bank: undefined,
        address: undefined,
        fio: undefined
      },

      suggestions: {
        inn: [],
        bank: [],
        address: []
      },

      loading: {
        owner: false,
        inn: false,
        bank: false,
        address: false
      }
    }
  },

  computed: {
    $OwnerId() {
      return this.proxy.OwnerId || get(this.proxy, 'Owner.id') || this.OwnerId || this.account.id
    },

    basis() {
      const result = [ RequisitesBases.certificate ]
      if (this.suggestion.inn.legal?.ogrn) {
        result.push(`№${this.suggestion.inn.legal?.ogrn}`)
      }
      if (this.suggestion.inn.legal?.ogrn_date) {
        result.push(`от ${getRUDate(this.suggestion.inn.legal?.ogrn_date)}`)
      }

      return getDeclensions(result.join(' ').trim())
    },

    phoneValidation() {
      if (this.proxy.contact.phone && !isValidPhone(this.proxy.contact.phone)) {
        return this.getTranslate(`${Services.requisites}.errors.contact.phone`)
      }
    },
    emailValidation() {
      if (this.proxy.contact.email && !isValidEmail(this.proxy.contact.email)) {
        return this.getTranslate(`${Services.requisites}.errors.contact.email`)
      }
    },
    contactValidation() {
      let result = true

      if (!this.proxy.contact.lastName) {
        result = false
      }
      if (!this.proxy.contact.firstName) {
        result = false
      }
      if (!this.proxy.contact.email || !!this.emailValidation) {
        result = false
      }
      if (!this.proxy.contact.phone || !!this.phoneValidation) {
        result = false
      }

      return result
    },
    signerValidation() {
      let result = true

      if (!this.proxy.signer.fullName.nominative) {
        result = false
      }
      if (!this.proxy.signer.position.nominative) {
        result = false
      }
      if (!this.proxy.signer.basis.nominative) {
        result = false
      }
      if (!this.proxy.signer.fullName.genitive) {
        result = false
      }
      if (!this.proxy.signer.position.genitive) {
        result = false
      }
      if (!this.proxy.signer.basis.genitive) {
        result = false
      }

      return result
    },
    legalValidation() {
      if (!this.proxy.data.legal.opf) {
        return false
      }
      if (!this.proxy.data.legal.name) {
        return false
      }
      if (!innRegExp.test(this.proxy.data.legal.inn)) {
        return false
      }
      if (!ogrnRegExp.test(this.proxy.data.legal.ogrn)) {
        return false
      }

      return true
    },
    bankValidation() {
      if (!this.proxy.data.bank.name) {
        return false
      }

      if (!rsRegExp.test(this.proxy.data.bank.rs)) {
        return false
      }
      if (!ksRegExp.test(this.proxy.data.bank.ks)) {
        return false
      }
      if (!bikRegExp.test(this.proxy.data.bank.bik)) {
        return false
      }

      return true
    },
    legalAddressValidation() {
      if (!this.proxy.data.legalAddress.country) {
        return false
      }
      if (!this.proxy.data.legalAddress.city) {
        return false
      }
      if (!indexRegExp.test(this.proxy.data.legalAddress.index)) {
        return false
      }

      return true
    },
    postalAddressValidation() {
      if (!this.proxy.data.postalAddress.country) {
        return false
      }
      if (!this.proxy.data.postalAddress.city) {
        return false
      }
      if (!indexRegExp.test(this.proxy.data.postalAddress.index)) {
        return false
      }

      return true
    },
    validation() {
      if (!this.proxy.title) {
        return false
      }

      if (!this.proxy.type || !Object.values(RequisiteTypes).includes(this.proxy.type)) {
        return false
      }

      return this.signerValidation && this.legalValidation && this.bankValidation && this.legalAddressValidation && this.postalAddressValidation
    }
  },

  watch: {
    contactMatches(value) {
      if (value) {
        this.getOwner(this.$OwnerId)
      }
    },

    validation() {
      this.$emit('validation', this.validation)
    },

    'proxy.data.legal.opf'() {
      this.setTitle()
    },

    'proxy.data.legal.name'() {
      this.setTitle()
    },

    'suggestion.inn': {
      handler(value) {
        if (value) {
          this.proxy.data.legal.opf = value.legal?.opf
          this.proxy.data.legal.name = value.legal?.name
          this.proxy.data.legal.inn = value.legal?.inn
          this.proxy.data.legal.ogrn = value.legal?.ogrn
          this.proxy.data.legal.kpp = value.legal?.kpp
          this.proxy.data.legal.okpo = value.legal?.okpo

          this.suggestion.fio = value.manager?.fullName

          let position = RequisitesPositions.ceo
          if (upperFirst(value.legal?.opf) === 'Индивидуальный предприниматель') {
            position = RequisitesPositions.manager
            this.proxy.signer.basis = this.basis
          } else {
            this.proxy.signer.basis = getDeclensions(RequisitesBases.charter)
          }
          if (value.manager?.post) {
            position = value.manager.post
          }
          this.proxy.signer.position = getDeclensions(position)

          if (value.address) {
            for (const key of Object.keys(this.proxy.data.legalAddress)) {
              this.proxy.data.legalAddress[key] = value.address[key]
            }
          }
        }
      },
      deep: true
    },

    'suggestion.bank': {
      handler(value) {
        if (value) {
          this.proxy.data.bank.name = value.name
          this.proxy.data.bank.ks = value.ks
          this.proxy.data.bank.bik = value.bik
        }
      },
      deep: true
    },

    'suggestion.fio'() {
      this.cleanFIO()
    },

    'suggestion.address': {
      handler(value) {
        if (value) {
          for (const key of Object.keys(this.proxy.data.legalAddress)) {
            this.proxy.data.legalAddress[key] = value.data[key]
          }
        }
      },
      deep: true
    },

    'proxy.OwnerId'(value) {
      if (value) {
        this.getOwner(value)
      } else {
        this.setContact(undefined)
        this.contactMatches = false
      }
    }
  },

  mounted() {
    this.proxy.type = RequisiteTypes.business
    this.setOwnerId()
    this.$emit('validation', this.validation)
  },

  methods: {
    setOwnerId() {
      if (this.$OwnerId) {
        this.proxy.OwnerId = this.$OwnerId
        this.getOwner(this.$OwnerId)
      }
    },

    setContact(user) {
      for (const key in this.proxy.contact) {
        this.proxy.contact[key] = get(user, `data[${key}]`)
      }
    },

    setTitle() {
      const title = []
      if (this.proxy.data.legal.opf) {
        const split = this.proxy.data.legal.opf.split(' ')
        const opfShort = split.reduce((result, word) => {
          if (word.length > 1) {
            result += word.substring(0, 1).toUpperCase()
          }

          return result
        }, '')
        title.push(opfShort)
      }
      if (this.proxy.data.legal.name) {
        title.push(`"${this.proxy.data.legal.name}"`)
      }
      this.proxy.title = title.join(' ')
    },

    async getOwner(id) {
      if (isUUID(id)) {
        try {
          if (this.contactMatches) {
            this.loading.owner = true
          }
          const data = await request.get(joinURLPath(usersPathToAPI, id))
          if (data) {
            this.Owner = data
            if (this.contactMatches) {
              this.setContact(data)
            }
          }
        } catch (error) {
          globalErrorHandler(error)
        } finally {
          if (this.contactMatches) {
            this.loading.owner = false
          }
        }
      }
    },

    /**
     * Записывает введённое пользователем значение поля подписанта в именительном падеже
     * и сразу заполняет родительный падеж. Пустое значение очищает оба падежа.
     *
     * @param {string} value Значение в именительном падеже.
     * @param {'position'|'basis'} key Поле подписанта.
     */
    setSignerNominative(value, key) {
      const { genitive } = getDeclensions(value)
      this.proxy.signer[key].nominative = value
      this.proxy.signer[key].genitive = genitive
    },

    /**
     * Записывает введённые пользователем ФИО в именительном падеже.
     * Пустое значение очищает ФИО в родительном падеже.
     *
     * @param {string} value ФИО в именительном падеже.
     */
    setFullNameNominative(value) {
      this.proxy.signer.fullName.nominative = value
      if (isEmpty(value)) {
        this.proxy.signer.fullName.genitive = ''
      }
    },

    getRequisitesByInn: debounce(
      async function(value) {
        const result = {
          title: undefined,
          value: undefined
        }

        if (!value) {
          return
        }

        const inputValue = value.toString()
        if (!innRegExp.test(inputValue)) {
          return
        }

        try {
          this.loading.inn = true
          const data = await request.find(joinURLPath(dadataPathToAPI, 'getRequisitesByInn', inputValue))
          if (data && Object.keys(data).length) {
            result.title = [ data.legal.inn, data.title ].join(', ')
            result.value = data
          }
          this.suggestions.inn.splice(0, this.suggestions.inn.length, result)
        } catch (error) {
          globalErrorHandler(error)
        } finally {
          this.loading.inn = false
        }
      },
      500,
      {
        leading: false,
        trailing: true
      }
    ),

    getSuggestions: debounce(
      async function(value, field) {
        const result = []

        if (!value || !field) {
          return
        }

        try {
          this.loading[field] = true
          const data = await request.find(
            joinURLPath(dadataPathToAPI, 'suggest', field),
            undefined,
            { query: value }
          )
          if (data.suggestions) {
            data.suggestions.map(suggestion => {
              const item = {}
              switch (field) {
                case 'address': {
                  delete suggestion.value
                  item.title = suggestion.unrestricted_value
                  item.value = suggestion
                  break
                }
                case 'bank': {
                  item.title = suggestion.name
                  item.value = suggestion
                  break
                }
              }
              result.push(item)
            })
            this.suggestions[field].splice(0, this.suggestions[field].length, ...result)
          }
        } catch (error) {
          globalErrorHandler(error)
        } finally {
          this.loading[field] = false
        }
      },
      500,
      {
        leading: false,
        trailing: true
      }
    ),

    async cleanFIO() {
      const result = {
        nominative: undefined,
        genitive: undefined
      }
      if (!this.suggestion.fio) {
        return
      }

      try {
        const data = await request.find(
          joinURLPath(dadataPathToAPI, 'clean', 'fio'),
          undefined,
          { query: this.suggestion.fio }
        )
        if (data) {
          result.nominative = data.nominative
          result.genitive = data.genitive
        }
        this.proxy.signer.fullName.nominative = result.nominative
        this.proxy.signer.fullName.genitive = result.genitive
      } catch (error) {
        globalErrorHandler(error)
      }
    },

    checkField(value, regexp, error) {
      if (value && !regexp.test(value)) {
        return error
      }

      return undefined
    }
  },

  render
}
